import { Divider, Flex, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { spacing } from '../../theming';
import { cardStyle, dotStyle, labelStyle, valueStyle } from './vinExtractionStyles';
import { useNumberFormat } from './useNumberFormat';
import StatusStripItem from './StatusStripItem';
import StatusCadence from './StatusCadence';
import StatusModelChip from './StatusModelChip';
import StatusEditButton from './StatusEditButton';

/**
 * The white strip under the header, one row (SC-QA-102): processing state, the next batch, the model, batch size,
 * threshold and polling, then Edit. The facts wrap inside the strip before Edit leaves the row.
 */
export default function StatusStrip({ settings, readOnly, onEdit }) {
    const { t } = useTranslation('vinExtraction');
    const { integer } = useNumberFormat();
    const { state, schedule } = settings;
    const nextRun = schedule.nextTriggerAt ? dayjs(schedule.nextTriggerAt).format('HH:mm') : '—';
    const divider = <Divider type="vertical" style={{ margin: 0 }} />;

    return (
        <Flex
            role="region"
            aria-label={t('status.aria')}
            align="center"
            justify="space-between"
            gap={spacing[2]}
            style={cardStyle}
        >
            <Flex align="center" gap={spacing[2]} wrap="wrap" style={{ flex: '1 1 0', minWidth: 0 }}>
                <Flex align="center" gap={spacing[1]}>
                    <span style={dotStyle(state)} aria-hidden="true" />
                    <span style={labelStyle}>{t('status.processing')}</span>
                    <Typography.Text strong>{t(`status.state.${state}`)}</Typography.Text>
                    {/* E28: the API reports `paused` over `polling_paused`, so a paused polling is named here too */}
                    {settings.pollingPaused === true && state !== 'polling_paused' && (
                        <Tag color="warning" bordered={false} style={{ margin: 0 }}>
                            {t('status.pollingAlsoPaused')}
                        </Tag>
                    )}
                </Flex>
                {divider}
                <StatusStripItem label={t('status.nextBatch')}>
                    <span style={valueStyle} data-testid="vin-next-run">
                        {nextRun}
                    </span>
                </StatusStripItem>
                {divider}
                <StatusStripItem label={t('status.model')}>
                    <StatusModelChip model={settings.model} modelAllowed={settings.modelAllowed} />
                </StatusStripItem>
                {divider}
                <StatusStripItem label={t('status.batchSize')}>
                    <span style={valueStyle}>{integer(settings.batchCarLimit)}</span>
                </StatusStripItem>
                {divider}
                <StatusStripItem label={t('status.threshold')}>
                    <span style={valueStyle}>{settings.confidenceThreshold.toFixed(2)}</span>
                </StatusStripItem>
                {divider}
                <StatusStripItem label={t('status.polling')}>
                    <StatusCadence cron={schedule.polling} />
                </StatusStripItem>
            </Flex>
            <StatusEditButton readOnly={readOnly} onEdit={onEdit} />
        </Flex>
    );
}
